/**
 * RunDetail — slide-over for a single GardenRun.
 *
 * Renders stage_outcomes (Dict[stage_name, summary_dict]), the run-level
 * diff_summary, and the acceptance_result (failures: string[]).
 */
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { gardenApi, type GardenRun, type GardenRunTask } from '@/services/api';
import { SlideOver } from '@/components/pdo/SlideOver';
import {
  RunStatusBadge,
  RunTypeBadge,
  StageStatusIcon,
} from '@/components/pdo/StatusBadges';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useState } from 'react';
import { useFetch, formatDuration, formatNumber, timeAgo } from '@/components/pdo/utils';

interface Props {
  clientUid: string;
  runId: string;
  onClose: () => void;
  onAction: () => void;
}

function durationFromTimestamps(
  startedAt: string | null,
  finishedAt: string | null,
): number | null {
  if (!startedAt || !finishedAt) return null;
  const s = new Date(startedAt).getTime();
  const f = new Date(finishedAt).getTime();
  if (Number.isNaN(s) || Number.isNaN(f)) return null;
  return f - s;
}

export function RunDetailPanel({ clientUid, runId, onClose }: Props) {
  const detail = useFetch(
    () => gardenApi.getRun(clientUid, runId),
    [clientUid, runId],
  );

  const r = detail.data;
  const duration = r ? durationFromTimestamps(r.started_at, r.finished_at) : null;

  return (
    <SlideOver
      open
      onClose={onClose}
      width="lg"
      title={
        <span className="flex items-center gap-2">
          <span className="font-mono">{runId}</span>
          {r && <RunStatusBadge status={r.status} />}
        </span>
      }
      subtitle={
        r ? (
          <span className="flex items-center gap-2">
            <RunTypeBadge runType={r.run_type} />
            <span className="text-slate-400">·</span>
            Started {timeAgo(r.started_at)}
            {duration && (
              <>
                <span className="text-slate-400">·</span>
                {formatDuration(duration)}
              </>
            )}
          </span>
        ) : (
          'Loading…'
        )
      }
      footer={
        r &&
        (r.result_version_id || r.compass_id) && (
          <div className="flex items-center justify-between gap-2">
            {r.result_version_id && (
              <Link
                to={`/admin/ontology/${clientUid}/versions?v=${r.result_version_id}`}
                className="text-xs font-medium text-teal-700 hover:underline"
              >
                View PDO version {r.result_version_id} →
              </Link>
            )}
            {r.compass_id && (
              <Link
                to={`/admin/garden/${clientUid}/compass?v1=${r.compass_id}`}
                className="text-xs font-medium text-purple-700 hover:underline ml-auto"
              >
                Compass {r.compass_id} →
              </Link>
            )}
          </div>
        )
      }
    >
      {detail.error && (
        <div className="px-5 py-4">
          <ErrorBanner error={detail.error} onRetry={detail.refetch} />
        </div>
      )}
      {detail.loading && !r ? (
        <div className="flex items-center justify-center py-16 text-xs text-slate-400">
          <Loader2 size={14} className="animate-spin mr-2" />
          Loading run detail…
        </div>
      ) : r ? (
        <RunBody detail={r} />
      ) : null}
    </SlideOver>
  );
}

function RunBody({ detail }: { detail: GardenRun }) {
  const stageEntries = Object.entries(detail.stage_outcomes ?? {});

  return (
    <div className="px-5 py-4 space-y-5">
      {/* Diff summary */}
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Diff summary
        </h4>
        <div className="grid grid-cols-5 gap-2 text-xs">
          <Tile
            label="+ concepts"
            value={formatNumber(detail.diff_summary.concepts_added)}
            tone="emerald"
          />
          <Tile
            label="− concepts"
            value={formatNumber(detail.diff_summary.concepts_removed)}
            tone="red"
          />
          <Tile
            label="Δ concepts"
            value={formatNumber(detail.diff_summary.concepts_modified)}
            tone="amber"
          />
          <Tile
            label="+ edges"
            value={formatNumber(detail.diff_summary.edges_added)}
            tone="emerald"
          />
          <Tile
            label="− edges"
            value={formatNumber(detail.diff_summary.edges_removed)}
            tone="red"
          />
        </div>
        {detail.diff_summary.auc_delta != null && (
          <p className="text-xs text-slate-600 mt-2">
            AUC delta:{' '}
            <span
              className={clsx(
                'font-mono font-semibold',
                detail.diff_summary.auc_delta > 0
                  ? 'text-emerald-700'
                  : detail.diff_summary.auc_delta < 0
                    ? 'text-red-700'
                    : 'text-slate-600',
              )}
            >
              {detail.diff_summary.auc_delta.toFixed(3)}
            </span>
          </p>
        )}
      </section>

      {/* Acceptance evaluation */}
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Acceptance evaluation
        </h4>
        <div
          className={clsx(
            'border rounded-lg p-3 mb-2 flex items-center gap-2 text-sm',
            detail.acceptance_result.passed
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800',
          )}
        >
          {detail.acceptance_result.passed ? (
            <CheckCircle2 size={14} />
          ) : (
            <XCircle size={14} />
          )}
          <span className="font-medium">
            {detail.acceptance_result.passed
              ? 'All acceptance criteria passed'
              : `${detail.acceptance_result.failures.length} criteria failed`}
          </span>
          {detail.acceptance_result.auto_promoted && (
            <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-emerald-700">
              auto-promoted
            </span>
          )}
        </div>
        {detail.acceptance_result.failures.length === 0 ? (
          <EmptyState compact title="No acceptance failures" />
        ) : (
          <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-xs">
            {detail.acceptance_result.failures.map((f, i) => (
              <li key={i} className="flex items-start gap-2 px-3 py-2">
                <XCircle size={12} className="text-red-600 flex-shrink-0 mt-0.5" />
                <span className="font-mono text-navy-900 break-words">{f}</span>
              </li>
            ))}
          </ul>
        )}
        {detail.acceptance_result.reviewed_by && (
          <p className="text-[11px] text-slate-500 mt-2">
            Reviewed by{' '}
            <span className="font-mono text-navy-900">
              {detail.acceptance_result.reviewed_by}
            </span>{' '}
            {detail.acceptance_result.reviewed_at && (
              <>at {timeAgo(detail.acceptance_result.reviewed_at)}</>
            )}
          </p>
        )}
      </section>

      {/* Stage outcomes */}
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Stage outcomes
        </h4>
        {stageEntries.length === 0 ? (
          <EmptyState compact title="No stage outcomes recorded" />
        ) : (
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {stageEntries.map(([name, oc]) => (
              <RunStageRow
                key={name}
                name={name}
                outcome={oc}
                isCurrent={detail.current_stage === name}
              />
            ))}
          </div>
        )}
      </section>

      {/* Curation-fleet run tree — per-creative (+ cascade) tasks */}
      {(detail.tasks_total ?? 0) > 0 && (
        <RunTasksSection
          clientUid={detail.client_uid}
          runId={detail.run_id}
          total={detail.tasks_total ?? 0}
        />
      )}

      {detail.acceptance_result.rejection_reason && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Rejection reason
          </h4>
          <p className="text-sm text-red-700 italic whitespace-pre-wrap">
            {detail.acceptance_result.rejection_reason}
          </p>
        </section>
      )}

      {detail.error && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Error
          </h4>
          <p className="text-sm text-red-700 font-mono whitespace-pre-wrap">
            {detail.error}
          </p>
        </section>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Curation-fleet run tree — per-creative (+ cascade) CurationTasks
// --------------------------------------------------------------------------

function _taskCountTone(status: string): string {
  if (status === 'done') return 'bg-emerald-50 text-emerald-700';
  if (status === 'failed') return 'bg-red-50 text-red-700';
  if (status === 'running' || status === 'dispatched')
    return 'bg-amber-50 text-amber-700';
  if (status === 'deferred' || status === 'saturated')
    return 'bg-slate-100 text-slate-600';
  return 'bg-slate-50 text-slate-500';
}

function TaskStatusIcon({ status }: { status: string }) {
  if (status === 'done')
    return <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />;
  if (status === 'failed')
    return <XCircle size={14} className="text-red-600 flex-shrink-0" />;
  if (status === 'running' || status === 'dispatched')
    return <Loader2 size={14} className="text-amber-600 animate-spin flex-shrink-0" />;
  return <span className="w-[14px] flex-shrink-0 text-slate-300 text-center">•</span>;
}

function RunTaskRow({ task }: { task: GardenRunTask }) {
  const target =
    (task.target_ref?.creative_id as string | undefined) ??
    (task.target_ref?.concept_name as string | undefined) ??
    JSON.stringify(task.target_ref ?? {});
  return (
    <div className="flex items-center gap-3 px-3 py-2 text-sm">
      <TaskStatusIcon status={task.status} />
      <span className="flex-1 min-w-0 truncate">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">
          {task.kind}
        </span>
        <span className="font-mono text-navy-900">{String(target)}</span>
        {task.depth > 0 && (
          <span className="ml-2 text-[10px] text-slate-400">depth {task.depth}</span>
        )}
        {task.error && (
          <span className="ml-2 text-[10px] text-red-600 italic">{task.error}</span>
        )}
      </span>
      {task.attempts > 1 && (
        <span className="text-[10px] font-mono text-amber-600 flex-shrink-0">
          ×{task.attempts}
        </span>
      )}
      <span className="text-xs font-mono text-slate-500 flex-shrink-0">
        {timeAgo(task.updated_at)}
      </span>
    </div>
  );
}

function RunTasksSection({
  clientUid,
  runId,
  total,
}: {
  clientUid: string;
  runId: string;
  total: number;
}) {
  const tasks = useFetch(
    () => gardenApi.getRunTasks(clientUid, runId),
    [clientUid, runId],
  );
  const data = tasks.data;
  return (
    <section>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
        Per-creative tasks{data ? ` (${data.total}/${total})` : ` (${total})`}
      </h4>
      {tasks.loading && !data ? (
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> loading tasks…
        </div>
      ) : tasks.error ? (
        <ErrorBanner error={tasks.error} onRetry={tasks.refetch} />
      ) : !data || data.tasks.length === 0 ? (
        <EmptyState compact title="No tasks recorded yet" />
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {Object.entries(data.counts).map(([status, n]) => (
              <span
                key={status}
                className={clsx(
                  'px-2 py-0.5 rounded-full text-[11px] font-mono',
                  _taskCountTone(status),
                )}
              >
                {status}: {n}
              </span>
            ))}
          </div>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {data.tasks.map((t) => (
              <RunTaskRow key={t.task_id} task={t} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function RunStageRow({
  name,
  outcome,
  isCurrent,
}: {
  name: string;
  outcome: Record<string, unknown>;
  isCurrent: boolean;
}) {
  const [open, setOpen] = useState(false);
  const status = String(outcome.status ?? 'pending');
  const startedAt = (outcome.started_at as string | null | undefined) ?? null;
  const finishedAt = (outcome.finished_at as string | null | undefined) ?? null;
  const elapsedMs =
    startedAt && finishedAt
      ? new Date(finishedAt).getTime() - new Date(startedAt).getTime()
      : null;
  const summary = (outcome.summary as Record<string, unknown> | undefined) ?? {};
  const errors = (outcome.errors as Array<Record<string, unknown>> | undefined) ?? [];
  const summaryEntries = Object.entries(summary).filter(
    ([, v]) => v != null && (typeof v !== 'object' || Array.isArray(v)),
  );

  return (
    <div className={clsx(isCurrent && 'bg-amber-50/40')}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50"
      >
        <ChevronDown
          size={14}
          className={clsx(
            'text-slate-400 transition-transform flex-shrink-0',
            !open && '-rotate-90',
          )}
        />
        <StageStatusIcon status={status} />
        <span className="text-sm font-medium text-navy-900 flex-1 truncate">
          {name}
          {isCurrent && (
            <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-amber-700">
              running
            </span>
          )}
        </span>
        <span className="text-xs font-mono text-slate-500">
          {elapsedMs != null ? formatDuration(elapsedMs) : '–'}
        </span>
      </button>
      {open && (
        <div className="px-9 pb-3 pt-1 space-y-2 text-xs">
          {summaryEntries.length > 0 ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {summaryEntries.slice(0, 16).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-mono text-slate-800 truncate ml-2">
                    {Array.isArray(v) ? `${v.length} items` : String(v)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 italic">No summary recorded</p>
          )}
          {errors.length > 0 && (
            <div className="space-y-1">
              {errors.map((e, i) => (
                <p key={i} className="text-red-600 font-mono text-[11px]">
                  {String((e as { message?: unknown }).message ?? JSON.stringify(e))}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'emerald' | 'red' | 'amber';
}) {
  return (
    <div
      className={clsx(
        'px-2.5 py-1.5 rounded-lg border',
        tone === 'emerald' && 'border-emerald-200 bg-emerald-50',
        tone === 'red' && 'border-red-200 bg-red-50',
        tone === 'amber' && 'border-amber-200 bg-amber-50',
      )}
    >
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
        {label}
      </p>
      <p
        className={clsx(
          'text-base font-mono font-semibold mt-0.5',
          tone === 'emerald' && 'text-emerald-700',
          tone === 'red' && 'text-red-700',
          tone === 'amber' && 'text-amber-700',
        )}
      >
        {value}
      </p>
    </div>
  );
}
